import type {
  CheckoutIntentOutput,
  CreateTicketInput,
  CreateTicketTypeInput,
  GetTicketOutput,
  Promo,
  RefundTicketInput,
  RefundTicketOutput,
  TemporalRelation,
  Ticket,
  TicketCheckoutData,
  TicketReceiptOutput,
  TicketType,
  TicketTypeOutput,
  TicketsApiCheckInUserTicketsEventsEventIdUsersUserIdCheckInPostRequest,
  TicketsApiCreateCheckoutIntentTicketsCheckoutCreateIntentPostRequest,
  UpdateTicketOutput,
} from '@sunrise-ui/api/events/api';
import { useAuthStore, useSnackbar } from '@sunrise-ui/primitives';
import type { QueryKey } from '@tanstack/react-query';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { QueryKeys } from '../constants/QueryKeys';
import {
  sessionsApi,
  ticketsApi,
  useAuthMutation,
  useAuthQuery,
} from '../utils/api';
import {
  assertParameterIsNotEmptyString,
  assertParameterIsNotUndefinedOrNull,
} from '../utils/error';
import { updateUserCheckInStatus } from '../utils/tickets';

const {
  ticketsKey,
  byEventIdKey,
  countKey,
  temporalKey,
  typeKey,
  receiptsKey,
  byUserKey,
  byReceiptIdKey,
  calculateTotalKey,
  checkoutStatusKey,
} = QueryKeys.tickets;

// Function to get tickets directly from API
const getTicketsByEventId = async ({
  eventId,
  communityId,
}: {
  eventId?: string;
  communityId?: string;
}): Promise<Ticket[]> => {
  assertParameterIsNotEmptyString(eventId, 'eventId');
  assertParameterIsNotEmptyString(communityId, 'communityId');

  return await ticketsApi
    .getTicketsByEventIdTicketsCommunitiesCommunityIdEventsEventIdGet({
      communityId: communityId as string,
      eventId: eventId as string,
    })
    .then((response) =>
      (response.data as GetTicketOutput[]).map(
        (t) =>
          ({
            // Normalize to Ticket using fields defined by the API type
            event_id: t.event_id,
            id: t.id,
            is_active: t.is_active,
            is_checked_in: t.is_checked_in,
            ticket_type_id: t.ticket_type_id,
            token: t.token,
            user_id: t.user_id,
            quantity: t.quantity,
            created_date: t.created_date,
          }) as Ticket,
      ),
    );
};

export const useGetTicketsByEventId = (params?: {
  eventId?: string;
  communityId?: string;
}) => {
  return useAuthQuery({
    queryFn: () => {
      return getTicketsByEventId({
        eventId: params?.eventId,
        communityId: params?.communityId,
      });
    },
    queryKey: [ticketsKey, byEventIdKey, params?.eventId, params?.communityId],
    enabled: Boolean(params?.eventId && params?.communityId),
  });
};

// Hook to fetch tickets from multiple events simultaneously
export function useGetMultiEventTickets(
  eventIds: string[] = [],
  communityId?: string,
) {
  // Use useQueries to run multiple ticket queries in parallel, but with direct API calls
  const ticketsQueries = useQueries({
    queries: eventIds.map((eventId) => {
      return {
        // Use the same query key structure as the original hook
        queryKey: [ticketsKey, byEventIdKey, eventId, communityId],
        // Use the direct API function, not the hook
        queryFn: () => getTicketsByEventId({ eventId, communityId }),
        enabled: !!eventId && !!communityId,
      };
    }),
  });

  // Process the query results
  const isLoading = ticketsQueries.some((query) => query.isLoading);
  const isError = ticketsQueries.some((query) => query.isError);

  // Return early with empty defaults if loading or error
  if (isLoading || isError) {
    return {
      isLoading,
      isError,
      ticketsData: [],
      userEventTicketsMap: new Map<string, Ticket[]>(),
      fullCheckedInUserIds: new Set<string>(),
      allUserIds: [],
    };
  }

  // Create a map of user IDs to their tickets from all events
  const userEventTicketsMap = new Map<string, Ticket[]>();

  // Process tickets directly into the user map
  ticketsQueries.forEach((query) => {
    if (query.data) {
      query.data.forEach((ticket: Ticket) => {
        const userId = ticket.user_id;

        if (!userEventTicketsMap.has(userId)) {
          userEventTicketsMap.set(userId, []);
        }
        userEventTicketsMap.get(userId)?.push(ticket);
      });
    }
  });

  // Extract all tickets for convenience
  const ticketsData = Array.from(userEventTicketsMap.values()).flat();

  // Determine which users are checked into all their events
  const fullCheckedInUserIds = new Set<string>();
  userEventTicketsMap.forEach((tickets, userId) => {
    const allTicketsCheckedIn = tickets.every((ticket) => ticket.is_checked_in);
    if (allTicketsCheckedIn && tickets.length > 0) {
      fullCheckedInUserIds.add(userId);
    }
  });

  return {
    isLoading,
    isError,
    ticketsData,
    userEventTicketsMap,
    fullCheckedInUserIds,
    allUserIds: Array.from(userEventTicketsMap.keys()),
  };
}

export type CheckInMutationArgs =
  TicketsApiCheckInUserTicketsEventsEventIdUsersUserIdCheckInPostRequest;

export const useCheckIn = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    async mutationFn({ userId, token, eventId }: CheckInMutationArgs) {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const response =
        await ticketsApi.checkInUserTicketsEventsEventIdUsersUserIdCheckInPost({
          userId,
          eventId,
          token,
        });
      return response.data;
    },
    onMutate: async ({ userId, eventId }: CheckInMutationArgs) => {
      // Optimistically update all caches for this event regardless of communityId
      const matching = queryClient.getQueriesData<Ticket[]>({
        queryKey: [ticketsKey, byEventIdKey, eventId],
      });
      const snapshots: Array<{ key: QueryKey; previousTickets?: Ticket[] }> =
        [];
      for (const [key] of matching) {
        const result = await updateUserCheckInStatus({
          userId,
          checkInStatus: true,
          ticketsQueryKey: key,
          queryClient,
        });
        snapshots.push({ key, previousTickets: result.previousTickets });
      }
      return { snapshots };
    },
    async onError(
      _error,
      _variables,
      context:
        | { snapshots?: Array<{ key: QueryKey; previousTickets?: Ticket[] }> }
        | undefined,
    ) {
      // Revert all snapshots
      context?.snapshots?.forEach(({ key, previousTickets }) => {
        queryClient.setQueryData(key, previousTickets);
      });
      showSnackbar({
        text: 'Failed to check in user',
        type: 'error',
      });
    },
  });
};

export const useUndoCheckIn = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    async mutationFn({ userId, eventId }: { userId: string; eventId: string }) {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const response =
        await ticketsApi.undoCheckInUserTicketsEventsEventIdUsersUserIdCheckInUndoPost(
          {
            userId: userId,
            eventId,
          },
        );
      return response.data;
    },
    onMutate: async ({
      userId,
      eventId,
    }: {
      userId: string;
      eventId: string;
    }) => {
      const matching = queryClient.getQueriesData<Ticket[]>({
        queryKey: [ticketsKey, byEventIdKey, eventId],
      });
      const snapshots: Array<{ key: QueryKey; previousTickets?: Ticket[] }> =
        [];
      for (const [key] of matching) {
        const result = await updateUserCheckInStatus({
          userId,
          checkInStatus: false,
          ticketsQueryKey: key,
          queryClient,
        });
        snapshots.push({ key, previousTickets: result.previousTickets });
      }
      return { snapshots };
    },
    async onError(
      _error,
      _vars,
      context:
        | { snapshots?: Array<{ key: QueryKey; previousTickets?: Ticket[] }> }
        | undefined,
    ) {
      context?.snapshots?.forEach(({ key, previousTickets }) => {
        queryClient.setQueryData(key, previousTickets);
      });
      showSnackbar({
        text: 'Failed to remove check in for user',
        type: 'error',
      });
    },
  });
};

export const useCreateTicketType = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    TicketType,
    unknown,
    { communityId: string; createTicketTypeInput: CreateTicketTypeInput }
  >({
    async mutationFn({ communityId, createTicketTypeInput }) {
      const response =
        await ticketsApi.createTicketTypeTicketsCommunitiesCommunityIdTypePost({
          communityId,
          createTicketTypeInput,
        });
      return response.data;
    },
    async onSuccess(ticketType) {
      const { event_id } = ticketType;
      // Invalidate queries to refresh the ticket types list
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, typeKey, byEventIdKey, event_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, typeKey, event_id],
      });

      showSnackbar({
        text: 'Ticket type created successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create ticket type. Please try again.',
        type: 'error',
      });
    },
  });
};

// Hook for updating ticket type
export const useUpdateTicketType = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    TicketTypeOutput,
    unknown,
    {
      communityId: string;
      ticketTypeId: string;
      updateData: Partial<CreateTicketTypeInput>;
    }
  >({
    async mutationFn({ communityId, ticketTypeId, updateData }) {
      assertParameterIsNotEmptyString(ticketTypeId, 'ticketTypeId');
      assertParameterIsNotUndefinedOrNull(updateData, 'updateData');

      const response =
        await ticketsApi.updateTicketTypeTicketsCommunitiesCommunityIdTypeTicketTypeIdPut(
          {
            communityId,
            ticketTypeId,
            updateTicketTypeInput: updateData,
          },
        );

      return response.data;
    },
    async onSuccess(ticketType) {
      const { event_id } = ticketType;
      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, typeKey, byEventIdKey, event_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, typeKey, event_id],
      });

      showSnackbar({
        text: 'Ticket type updated successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update ticket type. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useCreateCheckoutIntent = () => {
  return useAuthMutation<
    CheckoutIntentOutput,
    unknown,
    TicketsApiCreateCheckoutIntentTicketsCheckoutCreateIntentPostRequest
  >({
    async mutationFn({ createCheckoutIntentInput, idempotencyKey }) {
      const response =
        await ticketsApi.createCheckoutIntentTicketsCheckoutCreateIntentPost({
          createCheckoutIntentInput,
          idempotencyKey,
        });
      return response.data;
    },
  });
};

export const useGetCheckoutStatus = ({
  checkoutId,
}: {
  checkoutId?: string;
}) => {
  return useAuthQuery({
    queryKey: [ticketsKey, checkoutStatusKey, checkoutId],
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(checkoutId, 'checkoutId');
      const response =
        await ticketsApi.getCheckoutStatusTicketsCheckoutCheckoutIdStatusGet({
          checkoutId,
        });
      return response.data;
    },
    enabled: Boolean(checkoutId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Poll every 1 second while pending or processing
      if (status === 'pending' || status === 'processing') {
        return 1000;
      }
      // Stop polling when completed or failed
      return false;
    },
  });
};

export const useCreateTicket = ({
  onTicketCreated,
  autoFavoriteSingleSession = false,
}: {
  onTicketCreated?: (ticketId: string) => void;
  autoFavoriteSingleSession?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Ticket,
    unknown,
    { communityId: string; createTicketInput: CreateTicketInput }
  >({
    async mutationFn({ communityId, createTicketInput }) {
      const response =
        await ticketsApi.createTicketTicketsCommunitiesCommunityIdPost({
          communityId,
          createTicketInput,
        });
      return response.data;
    },
    async onSuccess(ticket, variables) {
      if (ticket.id && onTicketCreated) {
        onTicketCreated(ticket.id);
      }

      const { createTicketInput } = variables as {
        communityId: string;
        createTicketInput: CreateTicketInput;
      };
      const { event_id, user_id } = createTicketInput;
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, temporalKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, byEventIdKey, event_id],
      });

      // Auto-favorite the only session in an event when enabled and applicable
      if (autoFavoriteSingleSession) {
        try {
          const sessions = await sessionsApi
            .getSessionsByEventSessionsEventsEventIdGet({
              eventId: event_id,
            })
            .then(({ data }) => data.sessions);

          if (Array.isArray(sessions) && sessions.length === 1) {
            const [onlySession] = sessions;
            if (onlySession?.id) {
              await sessionsApi.favoriteSessionSessionsSessionIdFavoritePost({
                sessionId: onlySession.id,
                sessionFavoriteInput: { user_id },
              });
            }
          }
        } catch (error) {
          // Silently ignore to avoid blocking RSVP flow
          console.warn('Auto-favorite session failed:', error);
        }
      }
    },
    async onError() {
      showSnackbar({
        text: 'Failed to RSVP. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useTransferTicket = (ticket: Ticket, communityId: string) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(receiver: TicketCheckoutData) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response = await ticketsApi.transferTicketTicketsTransferPost({
        transferTicketInput: {
          event_id: ticket.event_id,
          user_id: ticket.user_id,
          ticket_id: ticket.id!,
          receiver_ticket: receiver,
          community_id: communityId,
        },
      });
      return response.data;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, byEventIdKey, ticket.event_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ticketsKey, 'receipts', byEventIdKey, ticket.event_id],
      });
      showSnackbar({
        text: 'Ticket transferred successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Transfer of ticket failed',
        type: 'error',
      });
    },
  });
};

export const useGetTicketCountForEvents = (eventIds?: string[]) => {
  const queryClient = useQueryClient();

  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventIds, 'eventIds');
      const response =
        await ticketsApi.countTicketsByEventsTicketsEventsCountPost({
          requestBody: eventIds,
        });

      // Populate individual caches so InPersonEventCard can read from them
      response.data.events?.forEach((event) => {
        queryClient.setQueryData(
          [ticketsKey, countKey, byEventIdKey, event.event_id],
          event.ticket_count,
        );
      });

      return response.data;
    },
    queryKey: [ticketsKey, countKey, eventIds],
    enabled: Boolean(eventIds && eventIds.length > 0),
  });
};

export const useGetTicketCountForEvent = (eventId?: string) => {
  return useQuery<number>({
    queryKey: [ticketsKey, countKey, byEventIdKey, eventId],
    queryFn: () => 0, // Placeholder - data comes from bulk query cache
    enabled: false, // Never fetches on its own, only reads from cache
    staleTime: Infinity,
  });
};

// Hook to get ticket types for a specific event
export const useGetTicketTypesByEventId = (params?: {
  eventId?: string;
  isOnSale?: boolean;
  isHidden?: boolean;
  isWaitlistable?: boolean;
}) => {
  return useQuery<(TicketType & { id: string })[]>({
    queryFn: async () => {
      const { eventId, isOnSale, isHidden, isWaitlistable } = params ?? {};
      assertParameterIsNotEmptyString(eventId, 'eventId');

      const { data } =
        await ticketsApi.getTicketTypesTicketsTypesEventsEventIdGet({
          eventId: eventId as string,
          isOnSale,
          isHidden,
          isWaitlistable,
        });
      return data
        ?.filter((ticketType) => ticketType.id)
        .sort((a, b) => a.price - b.price) as (TicketType & { id: string })[];
    },
    queryKey: [
      ticketsKey,
      typeKey,
      byEventIdKey,
      params?.eventId,
      params?.isOnSale,
      params?.isHidden,
      params?.isWaitlistable,
    ],
    enabled: Boolean(params?.eventId),
  });
};

// Hook to get ticket receipts for a specific event (for detailed usage tracking)
export const useGetTicketReceiptsByEvent = (eventId?: string) => {
  return useAuthQuery<TicketReceiptOutput[]>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(eventId, 'eventId');
      return await ticketsApi
        .getTicketReceiptsByEventTicketsReceiptsEventsEventIdGet({
          eventId: eventId as string,
        })
        .then((response) => response.data);
    },
    queryKey: [ticketsKey, 'receipts', byEventIdKey, eventId],
    enabled: Boolean(eventId),
  });
};

export const useGetUserTicketsTemporal = ({
  relation,
  enabled = true,
  userId,
}: {
  relation: TemporalRelation;
  userId?: string;
  enabled?: boolean;
}) => {
  const { currentUserId } = useAuthStore();
  const idToUse = userId || currentUserId;

  return useAuthQuery({
    async queryFn() {
      assertParameterIsNotUndefinedOrNull(idToUse, 'userId');
      const response =
        await ticketsApi.getUserTicketsTemporalTicketsTemporalTemporalRelationUsersUserIdGet(
          {
            userId: idToUse,
            temporalRelation: relation,
          },
        );
      return response.data;
    },
    queryKey: [ticketsKey, temporalKey, relation, idToUse],
    enabled: Boolean(enabled && idToUse),
  });
};

// Hook for changing a ticket's ticket_type_id
export const useChangeTicketType = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    UpdateTicketOutput,
    unknown,
    {
      ticketId: string;
      newTicketTypeId: string;
      eventId?: string;
    }
  >({
    async mutationFn({ ticketId, newTicketTypeId }) {
      assertParameterIsNotEmptyString(ticketId, 'ticketId');
      assertParameterIsNotEmptyString(newTicketTypeId, 'newTicketTypeId');

      const response = await ticketsApi.updateTicketTicketsTicketIdPatch({
        ticketId,
        updateTicketInput: {
          ticket_type_id: newTicketTypeId,
        },
      });

      return response.data;
    },
    async onSuccess(_, variables) {
      const { eventId } = variables;

      // Invalidate ticket receipts query to refresh the list
      if (eventId) {
        await queryClient.invalidateQueries({
          queryKey: [ticketsKey, 'receipts', byEventIdKey, eventId],
        });
        await queryClient.invalidateQueries({
          queryKey: [ticketsKey, byEventIdKey, eventId],
        });
      }

      showSnackbar({
        text: 'Ticket type changed successfully!',
        type: 'success',
      });
    },
    async onError(error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to change ticket type. Please try again.';
      showSnackbar({
        text: message,
        type: 'error',
      });
    },
  });
};

// Hook for refunding a ticket
export const useRefundTicket = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    RefundTicketOutput,
    unknown,
    {
      ticketId: string;
      communityId: string;
      refundTicketInput: RefundTicketInput;
      eventId?: string;
    }
  >({
    async mutationFn({ ticketId, communityId, refundTicketInput }) {
      assertParameterIsNotEmptyString(ticketId, 'ticketId');
      assertParameterIsNotEmptyString(communityId, 'communityId');

      const response =
        await ticketsApi.refundTicketTicketsTicketIdCommunitiesCommunityIdRefundPost(
          {
            ticketId,
            communityId,
            refundTicketInput,
          },
        );

      return response.data;
    },
    async onSuccess(_, variables) {
      const { eventId } = variables;

      // Invalidate ticket receipts query to refresh the list
      if (eventId) {
        await queryClient.invalidateQueries({
          queryKey: [ticketsKey, 'receipts', byEventIdKey, eventId],
        });
        await queryClient.invalidateQueries({
          queryKey: [ticketsKey, byEventIdKey, eventId],
        });
      }

      showSnackbar({
        text: 'Ticket refunded successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to refund ticket. Please try again.',
        type: 'error',
      });
    },
  });
};

// Hook for calculating order total with taxes (query-based for shared state)
export const useCalculateOrderTotal = (
  ticketQuantities: Map<string, number>,
  appliedPromo?: Promo,
) => {
  // Build input with stable ordering
  const input = useMemo(() => {
    const tickets = Array.from(ticketQuantities.entries())
      .sort(([a], [b]) => a.localeCompare(b)) // Sort for stable key
      .map(([ticketTypeId, quantity]) => ({
        ticket_type_id: ticketTypeId,
        quantity,
      }));

    return tickets.length > 0
      ? {
          tickets,
          promo_code: appliedPromo?.discount_code,
        }
      : null;
  }, [ticketQuantities, appliedPromo]);

  // Serialize input for stable key comparison
  const inputKey = input ? JSON.stringify(input) : null;

  return useQuery({
    queryKey: [calculateTotalKey, inputKey],
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(input, 'input');
      const response = await ticketsApi.calculateTotalTicketsCalculateTotalPost(
        {
          calculateTotalInput: input,
        },
      );
      return response.data;
    },
    enabled: !!input && (input.tickets?.length ?? 0) > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes - calculations don't change often
  });
};

// Hook to get a single ticket receipt by ID
export const useGetTicketReceipt = (receiptId?: string) => {
  return useAuthQuery<TicketReceiptOutput>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(receiptId, 'receiptId');
      const response =
        await ticketsApi.getTicketReceiptTicketsReceiptsReceiptIdGet({
          receiptId: receiptId!,
        });
      return response.data;
    },
    queryKey: [ticketsKey, receiptsKey, byReceiptIdKey, receiptId],
    enabled: Boolean(receiptId),
  });
};

// Hook to get all ticket receipts for a specific event and user (group purchase)
export const useGetTicketReceiptsByEventAndUser = (params?: {
  eventId?: string;
  userId?: string;
}) => {
  return useAuthQuery<TicketReceiptOutput[]>({
    queryFn: async () => {
      assertParameterIsNotEmptyString(params?.eventId, 'eventId');
      assertParameterIsNotEmptyString(params?.userId, 'userId');
      const response =
        await ticketsApi.getTicketReceiptByEventAndUserTicketsReceiptsEventsEventIdUsersUserIdGet(
          {
            eventId: params!.eventId!,
            userId: params!.userId!,
          },
        );
      return response.data;
    },
    queryKey: [
      ticketsKey,
      receiptsKey,
      byEventIdKey,
      params?.eventId,
      byUserKey,
      params?.userId,
    ],
    enabled: Boolean(params?.eventId && params?.userId),
  });
};
